import {
  EntitySettingsDto,
  KnownProjectModuleSettingsDtoName,
  KnownProjectModuleSettingsDtoOccupantRules,
  KnownProjectModuleSettingsDtoStatus,
  ProjectModuleSettingsDto,
  UserDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { mapProjectModuleToExternalDeliveryType } from '../../project-modules/mappings';

export type ProjectModuleSettings = {
  id: string;
  data: ProjectModuleSettingsDto;
  schema: EntitySettingsDto['schema'];
};

export type UserCapabilities = {
  /** Modules explicitly set on the user. */
  explicitModules: KnownProjectModuleSettingsDtoName[];
  /** Modules inherited from the project. */
  implicitModules: KnownProjectModuleSettingsDtoName[];
};

// Only modules with an external Delivery Type mapping represent user-facing capabilities (and have an icon).
function toCapabilityModules(moduleNames: string[]): KnownProjectModuleSettingsDtoName[] {
  return moduleNames
    .map((name) => name as KnownProjectModuleSettingsDtoName)
    .filter((name) => mapProjectModuleToExternalDeliveryType(name) !== null);
}

/**
 * Computes the capabilities shown for a user directly from the UserDto.
 * The Users List endpoint (called with includeInheritedProjectModules) populates both the modules
 * granted directly to the user (projectModules) and the modules inherited from their project
 * (inheritedProjectModules), so the backend resolves inheritance and no project module settings
 * need to be fetched on the client. Used by the Users List page.
 */
export function getUserCapabilities(
  user: Pick<UserDto, 'projectModules' | 'inheritedProjectModules'>
): UserCapabilities {
  const explicitModules = toCapabilityModules(user.projectModules || []);
  const inheritedModules = toCapabilityModules(user.inheritedProjectModules || []);

  // A module granted directly to the user takes precedence over the inherited one, so it is only ever shown once.
  const implicitModules = inheritedModules.filter((name) => !explicitModules.includes(name));

  return { explicitModules, implicitModules };
}

/**
 * Computes the capabilities for a user from the project's module settings.
 * Used by the User Details page, which loads the user via the single GET endpoint that does not
 * populate inheritedProjectModules, so inheritance is derived from the project module settings instead.
 */
export function getUserCapabilitiesFromProjectModules(
  userProjectModules: string[],
  projectModules: ProjectModuleSettings[]
): UserCapabilities {
  const activeModules = projectModules.filter((m) => m.data.status === KnownProjectModuleSettingsDtoStatus.Active);

  const projectModulesAllowingAllUsers = activeModules.filter(
    (m) => m.data.occupantRules === KnownProjectModuleSettingsDtoOccupantRules.All
  );

  const projectModulesAllowingSelectOccupants = activeModules.filter(
    (m) => m.data.occupantRules === KnownProjectModuleSettingsDtoOccupantRules.Select
  );

  const implicitModules = toCapabilityModules(projectModulesAllowingAllUsers.map((m) => m.schema.name));

  // The explicit capabilities are based on the modules that allow selecting occupants,
  // as these are the modules where the user can be selected for deliveries.
  const explicitModules = toCapabilityModules(
    userProjectModules.filter((name) => projectModulesAllowingSelectOccupants.some((pm) => pm.schema.name === name))
  );

  return { explicitModules, implicitModules };
}
